import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { roundsAPI } from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import { isCumulativeRound, hasBowl, championLabel } from '../utils/eventTypes';

// Match FriDave's animated leaderboard exactly: absolute-positioned rows that
// slide (CSS `top` transition) with a phased highlight when the order changes,
// so live updates never snap or flash. CSS lives in index.css (.leaderboard-*).
const ROW_HEIGHT = 52;
const POLL_INTERVAL = 15000;

function BowlIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 48 54" fill="none" aria-label="Bowl" style={{ flexShrink: 0 }}>
      <circle cx="5.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <circle cx="42.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <path d="M7 13 Q24 8 41 13 L36 27 Q24 31 12 27 Z" fill="#C8C8C8" stroke="#A0A0A0" strokeWidth="0.8" strokeLinejoin="round"/>
      <ellipse cx="24" cy="13" rx="17" ry="3.5" fill="#E0E0E0" stroke="#A0A0A0" strokeWidth="0.7"/>
      <rect x="21.5" y="27" width="5" height="5" rx="1" fill="#C8C8C8"/>
      <ellipse cx="24" cy="33" rx="6.5" ry="3" fill="#D0D0D0"/>
      <rect x="14" y="36" width="20" height="3.5" rx="1" fill="#C0C0C0"/>
      <rect x="11" y="39.5" width="26" height="5" rx="2" fill="#B0B0B0"/>
    </svg>
  );
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const { socket, connected, joinRound, leaveRound } = useWebSocket();
  const [loading, setLoading] = useState(true);
  const [round, setRound] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLive, setIsLive] = useState(false);

  // Animation state (same shape as FriDaveLanding)
  const [highlightStates, setHighlightStates] = useState({});
  const [rankChanges, setRankChanges] = useState({});
  const [scorePopStates, setScorePopStates] = useState({});
  const prevOrderRef = useRef({});
  const prevPointsRef = useRef({});
  const flashTimerRef = useRef(null);
  const scorePopTimerRef = useRef(null);

  useEffect(() => {
    fetchLeaderboard();
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (scorePopTimerRef.current) clearTimeout(scorePopTimerRef.current);
    };
  }, []);

  // Apply a new leaderboard with the phased highlight→slide animation (FriDave
  // pattern). Used by both the socket and the poll so updates are always inline
  // and never blank/flash the page.
  const applyLeaderboardUpdate = useCallback((incoming) => {
    const list = Array.isArray(incoming) ? incoming : [];

    const newOrder = {};
    list.forEach((p, i) => { newOrder[p.player_id] = i; });

    const newChanges = {};
    const newScorePops = {};
    list.forEach((p) => {
      const oldIdx = prevOrderRef.current[p.player_id];
      const newIdx = newOrder[p.player_id];
      if (oldIdx !== undefined && oldIdx !== newIdx) {
        newChanges[p.player_id] = oldIdx - newIdx; // +ve = moved up
      }
      if (prevPointsRef.current[p.player_id] !== undefined &&
          prevPointsRef.current[p.player_id] !== p.total_points) {
        newScorePops[p.player_id] = true;
      }
    });

    prevPointsRef.current = Object.fromEntries(list.map(p => [p.player_id, p.total_points]));

    // Score pop — fire immediately, clear after the animation
    if (Object.keys(newScorePops).length > 0) {
      if (scorePopTimerRef.current) clearTimeout(scorePopTimerRef.current);
      setScorePopStates(newScorePops);
      scorePopTimerRef.current = setTimeout(() => setScorePopStates({}), 600);
    }

    if (Object.keys(newChanges).length > 0) {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      // Phase 1: highlight the rows about to move
      setHighlightStates(newChanges);
      // Phase 2: after 300ms swap order and trigger the slide
      flashTimerRef.current = setTimeout(() => {
        prevOrderRef.current = newOrder;
        setLeaderboard(list);
        setHighlightStates({});
        setRankChanges(newChanges);
        // Phase 3: clear the moving state once the slide completes
        flashTimerRef.current = setTimeout(() => setRankChanges({}), 750);
      }, 300);
    } else {
      prevOrderRef.current = newOrder;
      setLeaderboard(list);
    }
  }, []);

  // Silent leaderboard-only refetch (no spinner) that feeds the animation.
  const refreshLeaderboardSilently = useCallback(async (roundId) => {
    if (!roundId) return;
    const lb = await roundsAPI.getLeaderboard(roundId).catch(() => null);
    if (lb) applyLeaderboardUpdate(lb);
  }, [applyLeaderboardUpdate]);

  // WebSocket live updates
  useEffect(() => {
    if (!socket || !connected || !round || !isLive) return;

    joinRound(round.id);

    socket.on('score:updated', () => {
      refreshLeaderboardSilently(round.id);
    });

    socket.on('leaderboard:updated', (data) => {
      if (data && data.leaderboard) {
        applyLeaderboardUpdate(data.leaderboard);
      } else {
        refreshLeaderboardSilently(round.id);
      }
    });

    socket.on('round:completed', () => {
      fetchLeaderboard(true); // silent — flips status to completed, no spinner
    });

    return () => {
      socket.off('score:updated');
      socket.off('leaderboard:updated');
      socket.off('round:completed');
      if (round) leaveRound(round.id);
    };
  }, [socket, connected, round, isLive, refreshLeaderboardSilently, applyLeaderboardUpdate]);

  // Inline poll (FriDave pattern) — resilience if the socket drops. Silent.
  useEffect(() => {
    if (!round || !isLive) return;
    const interval = setInterval(() => refreshLeaderboardSilently(round.id), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [round, isLive, refreshLeaderboardSilently]);

  // Seed the animation refs from a freshly fetched leaderboard so the first
  // live update diffs correctly.
  const seedRefs = (list) => {
    const order = {};
    const points = {};
    (list || []).forEach((p, i) => { order[p.player_id] = i; points[p.player_id] = p.total_points; });
    prevOrderRef.current = order;
    prevPointsRef.current = points;
  };

  const fetchLeaderboard = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      // First check for an active round
      try {
        const activeRound = await roundsAPI.getActive();
        if (activeRound && activeRound.id) {
          const [roundDetails, leaderboardData] = await Promise.all([
            roundsAPI.getById(activeRound.id),
            roundsAPI.getLeaderboard(activeRound.id)
          ]);

          setRound(roundDetails);
          setLeaderboard(leaderboardData || []);
          seedRefs(leaderboardData || []);
          setIsLive(true);
          if (!silent) setLoading(false);
          return;
        }
      } catch (activeErr) {
        // No active round is okay, continue to completed rounds
      }

      // No active round — fetch most recent completed round (any type)
      const completedRounds = await roundsAPI.getAll({ status: 'completed' });

      if (completedRounds && completedRounds.length > 0) {
        const sortedRounds = completedRounds.sort((a, b) =>
          new Date(b.start_date) - new Date(a.start_date)
        );
        const mostRecentRound = sortedRounds[0];

        const [roundDetails, leaderboardData] = await Promise.all([
          roundsAPI.getById(mostRecentRound.id),
          roundsAPI.getLeaderboard(mostRecentRound.id)
        ]);

        setRound(roundDetails);
        setLeaderboard(leaderboardData || []);
        seedRefs(leaderboardData || []);
        setIsLive(false);
      } else {
        setRound(null);
        setLeaderboard([]);
      }
    } catch (err) {
      console.error('[Leaderboard] Failed to fetch leaderboard:', err);
      setRound(null);
      setLeaderboard([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!round) {
    return (
      <div>
        <div className="px-4 pt-4">
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏆</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Events Yet</h2>
              <p className="text-lg text-gray-600 mb-6">
                There are no completed events to display. Create and complete an event to see the leaderboard.
              </p>
              <button
                onClick={() => navigate('/events')}
                className="px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-secondary transition-colors"
              >
                Go to Events
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const roundLabel = round.round_type === 'practice' ? 'Practice Round' :
                     round.round_type === 'championship' ? 'Championship Round' :
                     'Round';

  // Compute display positions + ties (TML rule: tie = same points AND strokes)
  const withPos = [];
  leaderboard.forEach((player, index) => {
    let displayPosition = index + 1;
    let isTied = false;
    if (index > 0) {
      const prev = leaderboard[index - 1];
      if (player.total_points === prev.total_points && player.total_strokes === prev.total_strokes) {
        displayPosition = withPos[index - 1].displayPosition;
        isTied = true;
      }
    }
    withPos.push({ ...player, displayPosition, isTied });
  });

  // Winner + Bowl labels on a completed cumulative event. Winner = the player
  // in 1st on cumulative total (playoff already resolved server-side). The Bowl
  // is TML-only, so guest events get the winner label and nothing else.
  const isCompleted = !isLive;
  const showBowl = hasBowl(round.event_type);
  const isChampionshipR2Plus = isCumulativeRound(round) &&
    leaderboard.some(p => Number(p.total_points) !== Number(p.current_round_points));
  const championId = isCompleted && isChampionshipR2Plus && withPos[0] && !withPos[0].isTied
    ? withPos[0].player_id : null;
  const bowlWinnerId = isCompleted && isChampionshipR2Plus && showBowl
    ? withPos.find(p => p.is_bowl)?.player_id ?? null : null;

  return (
    <div className="pb-20">
      {/* Round Info */}
      <div className="px-4 mb-4">
        <Card>
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900">{round.event_name || 'Event'}</h2>
            <p className="text-base font-semibold text-primary">{roundLabel}</p>
            <p className="text-sm text-gray-600">{round.course_name}</p>
            {isLive ? (
              <div className="mt-2 inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded animate-pulse">
                LIVE
              </div>
            ) : (
              <div className="mt-2 inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded">
                COMPLETED
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Leaderboard */}
      <div className="px-4">
        <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
          {/* Column Headers */}
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-300 flex items-center gap-3">
            <div className="flex-shrink-0 w-12 text-center">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Pos</span>
            </div>
            <div className="flex-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Player</span>
            </div>
            <div className="flex-shrink-0 text-center w-16">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Total</span>
            </div>
            <div className="flex-shrink-0 text-center w-16">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Thru</span>
            </div>
          </div>

          {/* Players List */}
          {withPos.length === 0 ? (
            <div className="p-6 bg-white">
              <p className="text-gray-600 text-center">No scores recorded</p>
            </div>
          ) : isLive ? (
            /* Animated absolute-position rows for the live round */
            <div className="leaderboard-container" style={{ height: withPos.length * ROW_HEIGHT }}>
              {withPos.map((player, index) => {
                const change = rankChanges[player.player_id] || 0;
                const isHighlighted = !!highlightStates[player.player_id];
                const isMoving = !!rankChanges[player.player_id];
                const extraClass = isMoving ? 'leaderboard-row--moving' : isHighlighted ? 'leaderboard-row--highlight' : '';
                return (
                  <button
                    key={player.player_id}
                    onClick={() => navigate(`/rounds/${round.id}/players/${player.player_id}`)}
                    className={`leaderboard-row px-4 flex items-center gap-3 border-b border-gray-200 text-left w-full bg-white hover:bg-gray-50 ${extraClass}`}
                    style={{ top: index * ROW_HEIGHT, height: ROW_HEIGHT }}
                  >
                    <div className="flex-shrink-0 w-12 text-center">
                      <span className="text-sm font-semibold text-gray-700">
                        {player.isTied && <span className="text-xs">T</span>}{player.displayPosition}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-1">
                      <span className="text-base font-semibold text-gray-900 truncate">{player.player_name}</span>
                      {showBowl && player.is_bowl && <BowlIcon />}
                      {change > 0 && (
                        <svg className="w-3 h-3 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                      {change < 0 && (
                        <svg className="w-3 h-3 text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-center w-16">
                      <span className={`text-base font-semibold text-red-600 ${scorePopStates[player.player_id] ? 'score-pop' : ''}`}>
                        {player.total_points}
                      </span>
                    </div>
                    <div className="flex-shrink-0 text-center w-16">
                      <span className="text-base text-gray-700">{player.holes_completed}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Static rows for completed rounds */
            withPos.map((player) => (
              <button
                key={player.round_player_id}
                onClick={() => navigate(`/rounds/${round.id}/players/${player.player_id}`)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 cursor-pointer text-left border-b border-gray-200 last:border-b-0 bg-white"
              >
                <div className="flex-shrink-0 w-12 text-center">
                  <span className="text-sm font-semibold text-gray-700">
                    {player.isTied && <span className="text-xs">T</span>}{player.displayPosition}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <h3 className="text-base font-semibold text-gray-900 truncate">{player.player_name}</h3>
                    {showBowl && player.is_bowl && <BowlIcon />}
                  </div>
                  {player.player_id === championId && (
                    <p className="text-xs font-semibold text-gray-600 mt-0.5">
                      {championLabel(round.event_type)}
                      {player.is_playoff_winner ? ' · winner after playoff' : ''}
                    </p>
                  )}
                  {player.player_id === bowlWinnerId && (
                    <p className="text-xs font-semibold text-gray-500 mt-0.5">
                      Winner of the Bowl
                      {player.is_bowl_playoff_winner ? ' · winner after playoff' : ''}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 text-center w-16">
                  <span className="text-base font-semibold text-red-600">{player.total_points}</span>
                </div>
                <div className="flex-shrink-0 text-center w-16">
                  <span className="text-base text-gray-700">{player.holes_completed}</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Shareable Round 2 points-race animation (TML 2026 only) */}
        {isCompleted && isChampionshipR2Plus && round.event_name === 'TML 2026' && (
          <a
            href="/round2-2026.html"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg shadow-sm hover:opacity-90 transition-opacity"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">▶</span>
              <div>
                <p className="text-sm font-bold">Watch the Round 2 points race</p>
                <p className="text-xs text-white/80">Hole-by-hole animation from the Round 1 standings</p>
              </div>
            </div>
            <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}
